import React, { useMemo, useState } from 'react';
import { useChatContext } from '../providers/ChatProvider';

/**
 * CareerIntelligencePanel
 *
 * Displays real-time career intelligence from the backend:
 *   - Overall career completeness score (from CareerCompletenessIndex)
 *   - Top-ranked conversation priority (current focus area)
 *   - Dimension-by-dimension breakdown (9 dimensions)
 *   - Clicking a dimension sends a focused coaching prompt
 *
 * Props:
 *   onSendPrompt  — (promptText: string) => void
 *   theme         — 'dark' | 'light'
 */

const DIMENSION_LABELS = {
  identity:   { label: 'Identity',   emoji: '👤', tip: 'Your basic contact information and target role' },
  experience: { label: 'Experience', emoji: '💼', tip: 'Work history quality and detail level' },
  projects:   { label: 'Projects',   emoji: '🚀', tip: 'Personal and professional projects' },
  skills:     { label: 'Skills',     emoji: '⚡', tip: 'Technical and soft skill coverage' },
  education:  { label: 'Education',  emoji: '🎓', tip: 'Academic background' },
  evidence:   { label: 'Evidence',   emoji: '📎', tip: 'Verified documents in your Career Vault' },
  ats:        { label: 'ATS Match',  emoji: '🎯', tip: 'How well your resume matches ATS systems' },
  quality:    { label: 'Quality',    emoji: '✨', tip: 'Content quality and impact language' },
  summary:    { label: 'Summary',    emoji: '📝', tip: 'Professional summary completeness' },
};

const URGENCY_COLORS = {
  critical: '#ef4444',
  high:     '#f59e0b',
  medium:   '#3b82f6',
  low:      '#10b981',
};

const URGENCY_BG = {
  critical: 'rgba(239,68,68,0.1)',
  high:     'rgba(245,158,11,0.1)',
  medium:   'rgba(59,130,246,0.1)',
  low:      'rgba(16,185,129,0.1)',
};

function ScoreBar({ pct, color, animate = true }) {
  return (
    <div style={{
      height: 4,
      borderRadius: 2,
      background: 'rgba(255,255,255,0.06)',
      overflow: 'hidden',
      flex: 1,
    }}>
      <div style={{
        height: '100%',
        width: `${Math.round(pct * 100)}%`,
        background: color,
        borderRadius: 2,
        transition: animate ? 'width 0.6s cubic-bezier(0.25,0.46,0.45,0.94)' : 'none',
      }} />
    </div>
  );
}

function dimensionColor(pct) {
  if (pct >= 0.8) return '#10b981';
  if (pct >= 0.6) return '#3b82f6';
  if (pct >= 0.4) return '#f59e0b';
  return '#ef4444';
}

export default function CareerIntelligencePanel({ onSendPrompt, theme = 'dark' }) {
  const { completeness, opportunities } = useChatContext();
  const T = theme === 'dark';
  const [expanded, setExpanded] = useState(true);

  const overall = useMemo(() => {
    if (!completeness) return null;
    // Weighted mean matching backend GapAnalyzer weights
    const w = { identity: 0.15, experience: 0.25, projects: 0.12, skills: 0.10,
                 education: 0.08, evidence: 0.10, ats: 0.10, quality: 0.05, summary: 0.05 };
    return Object.entries(w).reduce((sum, [k, wt]) => {
      return sum + wt * (completeness[`${k}_pct`] || 0);
    }, 0);
  }, [completeness]);

  const topPriority = opportunities?.[0] || null;
  const overallPct = overall != null ? Math.round(overall * 100) : null;
  const overallColor = overallPct >= 80 ? '#10b981' : overallPct >= 60 ? '#3b82f6' : overallPct >= 40 ? '#f59e0b' : '#ef4444';

  if (!completeness) {
    return (
      <div style={{
        padding: '16px',
        fontFamily: "'Inter', sans-serif",
        color: T ? 'rgba(255,255,255,0.35)' : '#94a3b8',
        fontSize: 11,
        textAlign: 'center',
      }}>
        <div style={{ fontSize: 22, marginBottom: 6 }}>🧠</div>
        Career intelligence loads after your first message.
      </div>
    );
  }

  return (
    <div style={{
      fontFamily: "'Inter', sans-serif",
      padding: '0 0 8px',
      display: 'flex',
      flexDirection: 'column',
      gap: 0,
    }}>
      {/* Header */}
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '12px 14px 10px',
          borderBottom: T ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.07)',
          cursor: 'pointer',
          userSelect: 'none',
        }}
        onClick={() => setExpanded(x => !x)}
      >
        <span style={{ fontSize: 14 }}>🧠</span>
        <span style={{ fontSize: 11.5, fontWeight: 700, color: T ? 'rgba(255,255,255,0.88)' : '#0f172a', flex: 1 }}>
          Career Intelligence
        </span>
        {/* Overall score pill */}
        <span style={{
          fontSize: 11, fontWeight: 700,
          color: overallColor,
          background: T ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
          padding: '2px 8px', borderRadius: 20,
        }}>
          {overallPct}%
        </span>
        <svg
          width={12} height={12} viewBox="0 0 12 12" fill="none"
          style={{ transition: 'transform 0.2s', transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
        >
          <path d="M2 4l4 4 4-4" stroke={T ? 'rgba(255,255,255,0.3)' : '#94a3b8'} strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      </div>

      {expanded && (
        <>
          {/* Overall score bar */}
          <div style={{ padding: '10px 14px 8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <ScoreBar pct={overall} color={overallColor} />
              <span style={{ fontSize: 10, fontWeight: 700, color: overallColor, flexShrink: 0, minWidth: 28 }}>
                {overallPct}%
              </span>
            </div>
            <p style={{ fontSize: 9.5, color: T ? 'rgba(255,255,255,0.3)' : '#94a3b8', margin: 0 }}>
              {overallPct >= 80 ? '🎉 Career profile looking strong!' :
               overallPct >= 60 ? '📈 Good progress — keep going' :
               overallPct >= 40 ? '🔥 Getting started — focus on experience' :
               '🚀 Let\'s build your career profile together'}
            </p>
          </div>

          {/* Top Priority */}
          {topPriority && (
            <div style={{ padding: '0 14px 10px' }}>
              <p style={{
                fontSize: 9, fontWeight: 700, color: T ? 'rgba(255,255,255,0.35)' : '#94a3b8',
                textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 6px',
              }}>
                Current Focus
              </p>
              <div
                style={{
                  padding: '8px 10px', borderRadius: 10,
                  background: URGENCY_BG[topPriority.urgency] || 'rgba(59,130,246,0.08)',
                  border: `1px solid ${URGENCY_COLORS[topPriority.urgency] || '#3b82f6'}30`,
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                }}
                onClick={() => onSendPrompt && onSendPrompt(
                  `Let's work on improving my ${topPriority.dimension}. ${topPriority.reason}`
                )}
                onMouseEnter={e => { e.currentTarget.style.opacity = '0.8'; }}
                onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                  <span style={{ fontSize: 10 }}>
                    {DIMENSION_LABELS[topPriority.dimension]?.emoji || '📋'}
                  </span>
                  <span style={{
                    fontSize: 11, fontWeight: 600,
                    color: T ? 'rgba(255,255,255,0.85)' : '#0f172a',
                    flex: 1,
                  }}>
                    {topPriority.title || `Improve ${topPriority.dimension}`}
                  </span>
                  <span style={{
                    fontSize: 9, fontWeight: 700,
                    color: URGENCY_COLORS[topPriority.urgency] || '#3b82f6',
                    textTransform: 'uppercase',
                  }}>
                    {topPriority.urgency}
                  </span>
                </div>
                {topPriority.reason && (
                  <p style={{
                    fontSize: 10, margin: 0,
                    color: T ? 'rgba(255,255,255,0.45)' : '#6b7280',
                    lineHeight: 1.4,
                  }}>
                    {topPriority.reason}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Dimension breakdown */}
          <div style={{ padding: '0 14px' }}>
            <p style={{
              fontSize: 9, fontWeight: 700, color: T ? 'rgba(255,255,255,0.35)' : '#94a3b8',
              textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 8px',
            }}>
              Dimensions
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
              {Object.entries(DIMENSION_LABELS).map(([key, meta]) => {
                const pct = completeness[`${key}_pct`] || 0;
                const color = dimensionColor(pct);
                const prompt = `Help me improve my ${meta.label.toLowerCase()} section.`;
                return (
                  <div
                    key={key}
                    title={meta.tip}
                    style={{ cursor: 'pointer' }}
                    onClick={() => onSendPrompt && onSendPrompt(prompt)}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                      <span style={{ fontSize: 10, flexShrink: 0 }}>{meta.emoji}</span>
                      <span style={{
                        fontSize: 10.5, fontWeight: 500, flex: 1,
                        color: T ? 'rgba(255,255,255,0.70)' : '#374151',
                      }}>
                        {meta.label}
                      </span>
                      <span style={{
                        fontSize: 9.5, fontWeight: 700, color, flexShrink: 0, minWidth: 28, textAlign: 'right',
                      }}>
                        {Math.round(pct * 100)}%
                      </span>
                    </div>
                    <ScoreBar pct={pct} color={color} />
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
